/**
 * Bloqueio com caminho de saída.
 *
 * A regra de corte: se a mensagem cita uma entidade que o usuário poderia criar, ela precisa
 * de caminho e vem para cá. Se descreve um estado impossível ("não é possível cancelar um
 * agendamento concluído"), o texto basta e continua sendo um Alert comum.
 *
 * A permissão de `resolve` é a de ESCRITA da entidade que falta, nunca a do fluxo atual:
 * quem agenda não é necessariamente quem vincula clínicas.
 */
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert, AlertTitle, Box, Button, List, ListItem, ListItemText, Typography } from '@mui/material';

import { useAcao } from '../../authorization/useVeredito';
import { usePermissions } from '../../hooks/usePermissions';
import type { PrerequisiteAction } from '../../types/prerequisite';

export type PrerequisiteMode = 'navigate' | 'modal';

export interface PrerequisiteResolve {
  label: string;
  permission: string;
  mode: PrerequisiteMode;
  route?: string;
  onResolve?: () => void;
  // Telas com preenchimento em andamento (o wizard de agendamento, ao esbarrar num
  // horário indisponível) usam isto para abrir o destino em nova aba — navegar na mesma
  // aba descartaria o que a pessoa já preencheu, sem caminho de volta.
  openInNewTab?: boolean;
}

export interface PrerequisiteBlockProps {
  title: string;
  reason: string;
  resolve?: PrerequisiteResolve;
  deniedHint?: string;
  outOfScope?: boolean;
  items?: string[];
  /**
   * Um botão por destino, quando quem sabe onde o bloqueio se resolve é o servidor. Cada
   * ação declara a permissão que a API do destino exige, e a consulta é por botão.
   */
  actions?: PrerequisiteAction[];
}

/**
 * O botão de uma ação do servidor, isolado em componente porque é ele que consulta a
 * permissão — e a consulta é por ação, no mesmo caminho da lista de pendências da emissão.
 * Sem a permissão sai o recado, e não um botão que responderia 403.
 */
const ActionButton: React.FC<{
  action: PrerequisiteAction;
  onNavigate: (route: string) => void;
}> = ({ action, onNavigate }) => {
  const resolver = useAcao(action.requiredPermission, action.label);

  if (resolver.disabled) {
    return (
      <Typography variant="body2">{`${resolver.tooltip} — peça a um administrador.`}</Typography>
    );
  }

  return (
    <Button color="warning" size="small" variant="outlined" onClick={() => onNavigate(action.route)}>
      {action.label}
    </Button>
  );
};

export const PrerequisiteBlock: React.FC<PrerequisiteBlockProps> = ({
  title,
  reason,
  resolve,
  deniedHint,
  outOfScope = false,
  items,
  actions,
}) => {
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();

  // Fora do escopo do tenant a entidade existe: oferecer criação produziria duplicidade.
  const canResolve = Boolean(resolve) && !outOfScope && hasPermission(resolve!.permission);

  // Guarda a origem para o retorno, no padrão já usado em LoginPage.tsx:75.
  const navegarGuardandoAOrigem = (route: string) => {
    navigate(route, { state: { from: { pathname: location.pathname } } });
  };

  const handleClick = () => {
    if (!resolve) return;
    if (resolve.mode === 'navigate' && resolve.route) {
      if (resolve.openInNewTab) {
        window.open(resolve.route, '_blank', 'noopener,noreferrer');
        return;
      }
      navegarGuardandoAOrigem(resolve.route);
      return;
    }
    resolve.onResolve?.();
  };

  return (
    <Alert
      severity="warning"
      action={
        canResolve ? (
          <Button color="warning" size="small" onClick={handleClick}>
            {resolve!.label}
          </Button>
        ) : undefined
      }
    >
      <AlertTitle>{title}</AlertTitle>
      <Typography variant="body2">{reason}</Typography>

      {items && items.length > 0 && (
        <List dense disablePadding sx={{ mt: 1 }}>
          {items.map((item) => (
            <ListItem key={item} disablePadding>
              <ListItemText primary={item} primaryTypographyProps={{ variant: 'body2' }} />
            </ListItem>
          ))}
        </List>
      )}

      {actions && actions.length > 0 && (
        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
          {actions.map((action) => (
            <ActionButton key={action.route} action={action} onNavigate={navegarGuardandoAOrigem} />
          ))}
        </Box>
      )}

      {!canResolve && deniedHint && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2">{deniedHint}</Typography>
        </Box>
      )}
    </Alert>
  );
};

export default PrerequisiteBlock;
